import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { LoginUseCase, LoginRequest, LoginResponse } from '../use-cases/LoginUseCase';
import { LogoutUseCase, LogoutRequest, LogoutResponse } from '../use-cases/LogoutUseCase';
import { CheckAuthUseCase, AuthStatusResponse, PermissionCheckResponse } from '../use-cases/CheckAuthUseCase';
import { User } from '../models/User';
import { AuthToken } from '../value-objects/AuthToken';
import { Session } from '../models/Session';

/**
 * 인증 상태 타입
 */
export type AuthState = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

/**
 * 사용자 정보 타입 (Store용)
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  organization_id?: string;
  organization_name?: string;
  profileImageUrl?: string;
  lastLoginAt?: Date;
  accountLocked?: boolean;
  passwordChangeRequired?: boolean;
}

/**
 * 토큰 정보 타입 (Store용)
 */
export interface AuthTokenXCircle {
  isValid: boolean;
  expiresAt: Date;
  expiresIn: number;
  needsRefresh: boolean;
  status: string;
}

/**
 * 세션 정보 타입 (Store용)
 */
export interface AuthSessionXCircle {
  id: string;
  isValid: boolean;
  expiresAt?: Date;
  lastAccessedAt?: Date;
  inactiveMinutes: number;
}

/**
 * 권한 정보 타입
 */
export interface PermissionXCircle {
  resource: string;
  action: string;
  hasPermission: boolean;
  reason?: string;
  checkedAt: Date;
}

/**
 * Auth Store 상태
 */
export interface AuthStoreState {
  // 인증 상태
  authState: AuthState;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastError: string | null;

  // 사용자 정보
  user: AuthUser | null;
  token: AuthTokenXCircle | null;
  session: AuthSessionXCircle | null;

  // 권한 캐시
  permissions: Map<string, PermissionXCircle>;
  
  // 설정
  rememberMe: boolean;
  autoRefreshEnabled: boolean;
  
  // 메타데이터
  lastLoginAt: Date | null;
  lastActivityAt: Date | null;
  loginAttempts: number;
  securityRecommendations: string[];

  // 타이머 및 구독
  refreshTimer: NodeJS.Timeout | null;
  activityTimer: NodeJS.Timeout | null;
  logoutTimer: NodeJS.Timeout | null;
}

/**
 * Auth Store 액션
 */
export interface AuthStoreActions {
  // 인증 액션
  login: (request: LoginRequest) => Promise<LoginResponse>;
  logout: (request?: LogoutRequest) => Promise<LogoutResponse>;
  checkAuth: () => Promise<AuthStatusResponse>;
  refreshAuth: () => Promise<boolean>;
  
  // 권한 액션
  checkPermission: (resource: string, action: string, context?: Record<string, any>) => Promise<boolean>;
  checkMultiplePermissions: (permissions: Array<{ resource: string; action: string; context?: Record<string, any> }>) => Promise<{ resource: string; action: string; hasPermission: boolean }[]>;
  checkRole: (requiredRole: string) => Promise<boolean>;
  
  // 유틸리티 액션
  clearError: () => void;
  updateActivity: () => void;
  resetState: () => void;
  
  // 타이머 관리
  setupAutoRefresh: () => void;
  setupActivityMonitor: () => void;
  setupAutoLogout: () => void;
  clearTimers: () => void;
  
  // 권한 캐시 관리
  clearPermissionCache: () => void;
  getPermissionFromCache: (resource: string, action: string) => PermissionXCircle | null;
  
  // 내부 상태 업데이트
  setAuthState: (state: AuthState) => void;
  setUser: (user: AuthUser | null) => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

/**
 * Auth Store 타입
 */
export type AuthStore = AuthStoreState & AuthStoreActions;

/**
 * 초기 상태
 */
const initialState: AuthStoreState = {
  authState: 'idle',
  isAuthenticated: false,
  isLoading: false,
  error: null,
  lastError: null,
  user: null,
  token: null,
  session: null,
  permissions: new Map(),
  rememberMe: false,
  autoRefreshEnabled: true,
  lastLoginAt: null,
  lastActivityAt: null,
  loginAttempts: 0,
  securityRecommendations: [],
  refreshTimer: null,
  activityTimer: null,
  logoutTimer: null,
};

/**
 * 권한 캐시 키 생성
 */
const createPermissionKey = (resource: string, action: string, context?: Record<string, any>): string => {
  const contextStr = context ? JSON.stringify(context) : '';
  return `${resource}:${action}:${contextStr}`;
};

/**
 * Auth Store 생성
 */
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      subscribeWithSelector(
        immer((set, get) => {
          // Use Case 인스턴스들
          const loginUseCase = new LoginUseCase();
          const logoutUseCase = new LogoutUseCase();
          const checkAuthUseCase = new CheckAuthUseCase();

          return {
            ...initialState,

            // 로그인
            login: async (request: LoginRequest): Promise<LoginResponse> => {
              set((state) => {
                state.authState = 'loading';
                state.isLoading = true;
                state.error = null;
                state.loginAttempts += 1;
              });

              try {
                const response = await loginUseCase.execute(request);

                set((state) => {
                  if (response.success && response.user && response.token && response.session) {
                    // 성공한 로그인 처리
                    state.authState = 'authenticated';
                    state.isAuthenticated = true;
                    state.isLoading = false;
                    state.error = null;
                    
                    // 사용자 정보 설정
                    state.user = {
                      id: response.user.id,
                      email: response.user.email,
                      name: response.user.name,
                      role: response.user.role,
                      isActive: response.user.isActive,
                      organization_id: response.user.organization_id,
                      organization_name: response.user.organization_name,
                      profileImageUrl: response.user.profileImageUrl,
                      lastLoginAt: response.user.lastLoginAt,
                    };
                    
                    // 토큰 정보 설정
                    state.token = {
                      isValid: true,
                      expiresAt: response.token.expiresAt,
                      expiresIn: response.token.expiresIn,
                      needsRefresh: false,
                      status: 'valid',
                    };
                    
                    // 세션 정보 설정
                    state.session = {
                      id: response.session.id,
                      isValid: true,
                      expiresAt: response.session.expiresAt,
                      lastAccessedAt: response.session.lastAccessedAt,
                      inactiveMinutes: 0,
                    };
                    
                    // 메타데이터 업데이트
                    state.lastLoginAt = new Date();
                    state.lastActivityAt = new Date();
                    state.rememberMe = request.rememberMe || false;
                    state.securityRecommendations = response.metadata?.recommendations || [];
                    
                    // 권한 캐시 초기화
                    state.permissions.clear();
                  } else {
                    // 실패한 로그인 처리
                    state.authState = 'unauthenticated';
                    state.isAuthenticated = false;
                    state.isLoading = false;
                    state.error = response.error || '로그인에 실패했습니다.';
                    state.lastError = response.error || null;
                  }
                });

                // 성공한 경우 타이머 설정
                if (response.success) {
                  get().setupAutoRefresh();
                  get().setupActivityMonitor();
                  get().setupAutoLogout();
                }

                return response;
              } catch (error) {
                set((state) => {
                  state.authState = 'error';
                  state.isLoading = false;
                  state.error = error instanceof Error ? error.message : '로그인 중 오류가 발생했습니다.';
                });

                return {
                  success: false,
                  error: error instanceof Error ? error.message : '로그인 중 오류가 발생했습니다.',
                  errorCode: 'LOGIN_ERROR',
                };
              }
            },

            // 로그아웃
            logout: async (request?: LogoutRequest): Promise<LogoutResponse> => {
              set((state) => {
                state.isLoading = true;
                state.error = null;
              });

              try {
                const response = await logoutUseCase.execute(request);

                set((state) => {
                  // 상태 초기화
                  state.authState = 'unauthenticated';
                  state.isAuthenticated = false;
                  state.isLoading = false;
                  state.user = null;
                  state.token = null;
                  state.session = null;
                  state.permissions.clear();
                  state.lastActivityAt = null;
                  state.securityRecommendations = [];
                });

                // 타이머 정리
                get().clearTimers();

                return response;
              } catch (error) {
                set((state) => {
                  state.isLoading = false;
                  state.error = error instanceof Error ? error.message : '로그아웃 중 오류가 발생했습니다.';
                  
                  // 오류가 발생해도 로컬 상태는 정리
                  state.authState = 'unauthenticated';
                  state.isAuthenticated = false;
                  state.user = null;
                  state.token = null;
                  state.session = null;
                  state.permissions.clear();
                });

                get().clearTimers();

                return {
                  success: false,
                  error: error instanceof Error ? error.message : '로그아웃 중 오류가 발생했습니다.',
                  errorCode: 'LOGOUT_ERROR',
                };
              }
            },

            // 인증 상태 확인
            checkAuth: async (): Promise<AuthStatusResponse> => {
              set((state) => {
                state.isLoading = true;
                state.error = null;
              });

              try {
                const response = await checkAuthUseCase.execute({ refreshIfNeeded: true });

                set((state) => {
                  if (response.isAuthenticated && response.user) {
                    state.authState = 'authenticated';
                    state.isAuthenticated = true;
                    state.user = {
                      id: response.user.id,
                      email: response.user.email,
                      name: response.user.name,
                      role: response.user.role,
                      isActive: response.user.isActive,
                      organization_id: response.user.organization_id,
                      organization_name: response.user.organization_name,
                      profileImageUrl: response.user.profileImageUrl,
                      lastLoginAt: response.user.lastLoginAt,
                      accountLocked: response.user.accountLocked,
                      passwordChangeRequired: response.user.passwordChangeRequired,
                    };
                    
                    if (response.token) {
                      state.token = response.token;
                    }
                    
                    if (response.session) {
                      state.session = response.session;
                    }
                    
                    if (response.security) {
                      state.securityRecommendations = response.security.recommendations || [];
                    }
                    
                    state.lastActivityAt = new Date();
                  } else {
                    state.authState = 'unauthenticated';
                    state.isAuthenticated = false;
                    state.user = null;
                    state.token = null;
                    state.session = null;
                    state.permissions.clear();
                  }
                  
                  state.isLoading = false;
                  state.error = response.error || null;
                });

                return response;
              } catch (error) {
                set((state) => {
                  state.authState = 'error';
                  state.isLoading = false;
                  state.error = error instanceof Error ? error.message : '인증 확인 중 오류가 발생했습니다.';
                });

                return {
                  isAuthenticated: false,
                  error: error instanceof Error ? error.message : '인증 확인 중 오류가 발생했습니다.',
                  errorCode: 'AUTH_CHECK_ERROR',
                };
              }
            },

            // 인증 갱신
            refreshAuth: async (): Promise<boolean> => {
              try {
                const response = await checkAuthUseCase.refreshToken();
                
                if (response.success && response.token) {
                  set((state) => {
                    state.token = response.token || null;
                    state.lastActivityAt = new Date();
                  });
                  
                  return true;
                }
                
                return false;
              } catch (error) {
                console.error('토큰 갱신 중 오류:', error);
                return false;
              }
            },

            // 권한 확인
            checkPermission: async (
              resource: string, 
              action: string, 
              context?: Record<string, any>
            ): Promise<boolean> => {
              const cacheKey = createPermissionKey(resource, action, context);
              
              // 캐시 확인 (5분 유효)
              const cached = get().permissions.get(cacheKey);
              if (cached && (Date.now() - cached.checkedAt.getTime()) < 5 * 60 * 1000) {
                return cached.hasPermission;
              }

              try {
                const response = await checkAuthUseCase.checkPermission({
                  resource,
                  action,
                  context,
                });

                const permissionXCircle: PermissionXCircle = {
                  resource,
                  action,
                  hasPermission: response.hasPermission,
                  reason: response.reason,
                  checkedAt: new Date(),
                };

                set((state) => {
                  state.permissions.set(cacheKey, permissionXCircle);
                });

                return response.hasPermission;
              } catch (error) {
                console.error('권한 확인 중 오류:', error);
                return false;
              }
            },

            // 다중 권한 확인
            checkMultiplePermissions: async (
              permissions: Array<{ resource: string; action: string; context?: Record<string, any> }>
            ): Promise<{ resource: string; action: string; hasPermission: boolean }[]> => {
              try {
                const response = await checkAuthUseCase.checkMultiplePermissions(permissions);
                
                // 캐시에 저장
                set((state) => {
                  response.results.forEach(result => {
                    const cacheKey = createPermissionKey(result.resource, result.action);
                    const permissionXCircle: PermissionXCircle = {
                      resource: result.resource,
                      action: result.action,
                      hasPermission: result.hasPermission,
                      reason: result.reason,
                      checkedAt: new Date(),
                    };
                    state.permissions.set(cacheKey, permissionXCircle);
                  });
                });

                return response.results;
              } catch (error) {
                console.error('다중 권한 확인 중 오류:', error);
                return permissions.map(p => ({
                  resource: p.resource,
                  action: p.action,
                  hasPermission: false,
                }));
              }
            },

            // 역할 확인
            checkRole: async (requiredRole: string): Promise<boolean> => {
              try {
                const response = await checkAuthUseCase.checkRoleAccess(requiredRole);
                return response.hasPermission;
              } catch (error) {
                console.error('역할 확인 중 오류:', error);
                return false;
              }
            },

            // 오류 초기화
            clearError: () => {
              set((state) => {
                state.error = null;
              });
            },

            // 활동 업데이트
            updateActivity: () => {
              set((state) => {
                state.lastActivityAt = new Date();
              });
              
              // Use Case를 통한 활동 시간 업데이트
              logoutUseCase.updateActivity();
            },

            // 상태 초기화
            resetState: () => {
              get().clearTimers();
              set((state) => {
                Object.assign(state, initialState);
              });
            },

            // 자동 갱신 설정
            setupAutoRefresh: () => {
              const state = get();
              if (state.refreshTimer) {
                clearInterval(state.refreshTimer);
              }

              const timer = setInterval(async () => {
                const currentState = get();
                if (currentState.isAuthenticated && currentState.autoRefreshEnabled) {
                  await get().refreshAuth();
                }
              }, 5 * 60 * 1000); // 5분마다

              set((state) => {
                state.refreshTimer = timer;
              });
            },

            // 활동 모니터 설정
            setupActivityMonitor: () => {
              const state = get();
              if (state.activityTimer) {
                clearInterval(state.activityTimer);
              }

              const timer = setInterval(() => {
                const currentState = get();
                if (currentState.isAuthenticated) {
                  // 비활성 시간 확인
                  const inactiveMinutes = currentState.lastActivityAt
                    ? Math.floor((Date.now() - currentState.lastActivityAt.getTime()) / (1000 * 60))
                    : 0;

                  if (inactiveMinutes >= 30) { // 30분 비활성시 자동 로그아웃
                    get().logout({ reason: 'timeout' });
                  }
                }
              }, 60 * 1000); // 1분마다 확인

              set((state) => {
                state.activityTimer = timer;
              });
            },

            // 자동 로그아웃 설정
            setupAutoLogout: () => {
              const state = get();
              if (state.logoutTimer) {
                clearTimeout(state.logoutTimer);
              }

              if (state.token?.expiresIn) {
                const timer = setTimeout(() => {
                  get().logout({ reason: 'timeout' });
                }, state.token.expiresIn * 1000);

                set((state) => {
                  state.logoutTimer = timer;
                });
              }
            },

            // 타이머 정리
            clearTimers: () => {
              const state = get();
              
              if (state.refreshTimer) {
                clearInterval(state.refreshTimer);
              }
              if (state.activityTimer) {
                clearInterval(state.activityTimer);
              }
              if (state.logoutTimer) {
                clearTimeout(state.logoutTimer);
              }

              set((state) => {
                state.refreshTimer = null;
                state.activityTimer = null;
                state.logoutTimer = null;
              });
            },

            // 권한 캐시 초기화
            clearPermissionCache: () => {
              set((state) => {
                state.permissions.clear();
              });
            },

            // 권한 캐시에서 조회
            getPermissionFromCache: (resource: string, action: string): PermissionXCircle | null => {
              const cacheKey = createPermissionKey(resource, action);
              const cached = get().permissions.get(cacheKey);
              
              // 5분 이내 캐시만 유효
              if (cached && (Date.now() - cached.checkedAt.getTime()) < 5 * 60 * 1000) {
                return cached;
              }
              
              return null;
            },

            // 내부 상태 업데이트 메서드들
            setAuthState: (authState: AuthState) => {
              set((state) => {
                state.authState = authState;
                state.isAuthenticated = authState === 'authenticated';
              });
            },

            setUser: (user: AuthUser | null) => {
              set((state) => {
                state.user = user;
              });
            },

            setError: (error: string | null) => {
              set((state) => {
                state.error = error;
                if (error) {
                  state.lastError = error;
                }
              });
            },

            setLoading: (loading: boolean) => {
              set((state) => {
                state.isLoading = loading;
              });
            },
          };
        })
      ),
      {
        name: 'auth-store',
        partialize: (state) => ({
          // 영속화할 상태만 선택
          user: state.user,
          rememberMe: state.rememberMe,
          lastLoginAt: state.lastLoginAt,
          autoRefreshEnabled: state.autoRefreshEnabled,
        }),
        version: 1,
        migrate: (persistedState: any, version: number) => {
          // 버전 마이그레이션 로직
          if (version === 0) {
            // 이전 버전에서 마이그레이션
            return {
              ...initialState,
              ...persistedState,
            };
          }
          return persistedState;
        },
      }
    ),
    {
      name: 'auth-store',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

// Store 구독자 설정
useAuthStore.subscribe(
  (state) => state.isAuthenticated,
  (isAuthenticated, prevIsAuthenticated) => {
    // 인증 상태 변경시 권한 캐시 초기화
    if (!isAuthenticated && prevIsAuthenticated) {
      useAuthStore.getState().clearPermissionCache();
    }
  }
);

// 초기화 함수
export const initializeAuthStore = async (): Promise<void> => {
  try {
    const authStore = useAuthStore.getState();
    
    // 현재 인증 상태 확인
    const authStatus = await authStore.checkAuth();
    
    if (authStatus.isAuthenticated) {
      // 인증된 상태라면 타이머 설정
      authStore.setupAutoRefresh();
      authStore.setupActivityMonitor();
      authStore.setupAutoLogout();
    }
  } catch (error) {
    console.error('AuthStore 초기화 중 오류:', error);
  }
};

// 정리 함수 (앱 종료시 호출)
export const cleanupAuthStore = (): void => {
  const authStore = useAuthStore.getState();
  authStore.clearTimers();
};